"use client";
import React, { useState, useRef, useEffect } from "react";
import { ChevronRight, Clock, FileText } from "lucide-react";
import api from "@/config/api";

const CMSPage = ({ apiEndpoint, pageTitle, defaultDescription }) => {
    const [activeSection, setActiveSection] = useState("");
    const [isManualScroll, setIsManualScroll] = useState(false);
    const sectionRefs = useRef({});
    const [cmsData, setCmsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sections, setSections] = useState([]);

    // Fetch CMS data
    useEffect(() => {
        const fetchCmsData = async () => {
            try {
                setLoading(true);
                const response = await api.get(apiEndpoint);
                
                if (response.data && response.data.status === 'success' && response.data.data) {
                    const data = response.data.data;
                    setCmsData(data);
                    
                    // Create sections from contents array
                    if (data.contents && Array.isArray(data.contents)) {
                        const sortedContents = data.contents.sort((a, b) => a.cms_c_number - b.cms_c_number);
                        const dynamicSections = sortedContents.map((content, index) => ({
                            id: content.cms_c_title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
                            title: content.cms_c_title,
                            number: (index + 1).toString(),
                            content: content.cms_c_content
                        }));
                        setSections(dynamicSections);
                    }
                }
            } catch (error) {
                console.error('Error fetching CMS data:', error);
                setError('Failed to load content. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchCmsData();
    }, [apiEndpoint]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (isManualScroll) return;

                const visibleSections = entries
                    .filter(entry => entry.isIntersecting)
                    .map(entry => ({
                        id: entry.target.id,
                        ratio: entry.intersectionRatio,
                        top: entry.boundingClientRect.top
                    }))
                    .sort((a, b) => {
                        if (Math.abs(a.top) < Math.abs(b.top)) return -1;
                        if (Math.abs(a.top) > Math.abs(b.top)) return 1;
                        return b.ratio - a.ratio;
                    });

                if (visibleSections.length > 0) {
                    setActiveSection(visibleSections[0].id);
                }
            },
            { 
                threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0], 
                rootMargin: "-100px 0px -30% 0px" 
            }
        );

        Object.values(sectionRefs.current).forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => observer.disconnect();
    }, [isManualScroll]);

    const scrollToSection = (id) => {
        const element = sectionRefs.current[id];
        if (element) {
            setIsManualScroll(true);
            setActiveSection(id);
            
            const offsetTop = element.offsetTop - 120;
            
            window.scrollTo({
                top: offsetTop,
                behavior: "smooth"
            });
            
            setTimeout(() => {
                setIsManualScroll(false);
            }, 1500);
        }
    };

    // Format date from cms_date
    const formatDate = (dateString) => {
        if (!dateString) return 'January 2025';
        const date = new Date(dateString);
        const options = { year: 'numeric', month: 'long' };
        return date.toLocaleDateString('en-US', options);
    };

    return (
        <>
            <style jsx global>{`
                .cms-content ul {
                    list-style-type: disc;
                    margin-left: 1.5rem;
                    margin-bottom: 1rem;
                }
                .cms-content li {
                    margin-bottom: 0.5rem;
                }
                .cms-content ul li {
                    display: list-item;
                }
                .cms-content p {
                    margin-bottom: 1rem;
                }
            `}</style>
            <div className="min-h-screen bg-gray-50">
                {/* Header Banner */}
                <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white py-12 sm:py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div>
                                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">
                                    {cmsData?.cms_title || pageTitle}
                                </h1>
                                <p className="text-slate-300 text-lg">Hyrelancer Platform</p>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-300">
                                <Clock className="w-4 h-4" />
                                <span>Effective Date: {formatDate(cmsData?.cms_date)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Sidebar Navigation */}
                        <aside className="lg:w-64 flex-shrink-0">
                            <div className="sticky top-32 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Table of Contents
                                </h3>
                                <nav className="space-y-1">
                                    {sections.map((section) => (
                                        <button
                                            key={section.id}
                                            onClick={() => scrollToSection(section.id)}
                                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                                                activeSection === section.id
                                                    ? "bg-blue-50 text-blue-700 font-medium"
                                                    : "text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            <span className="text-xs font-semibold">{section.number}.</span>
                                            <span className="flex-1">{section.title}</span>
                                            {activeSection === section.id && (
                                                <ChevronRight className="w-4 h-4" />
                                            )}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </aside>

                        {/* Main Document */}
                        <main className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200">
                            <div className="p-6 sm:p-8 lg:p-12 max-w-4xl">
                                {loading ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : error ? (
                                    <div className="text-center py-12">
                                        <p className="text-red-600 mb-4">{error}</p>
                                        <button 
                                            onClick={() => window.location.reload()} 
                                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {/* Introduction */}
                                        {cmsData?.cms_dec && (
                                            <div className="mb-8 pb-8 border-b border-gray-200">
                                                <div 
                                                    className="text-lg text-gray-700 leading-relaxed cms-content"
                                                    dangerouslySetInnerHTML={{ __html: cmsData.cms_dec }}
                                                />
                                            </div>
                                        )}

                                        {/* Dynamic Sections */}
                                        {sections.map((section) => (
                                            <section
                                                key={section.id}
                                                id={section.id}
                                                ref={(el) => (sectionRefs.current[section.id] = el)}
                                                className="mb-12 pb-12 border-b border-gray-200 last:border-b-0"
                                            >
                                                <div className="flex items-start gap-3 mb-4">
                                                    <span className="text-2xl font-bold text-blue-600">{section.number}.</span>
                                                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                                                </div>
                                                <div 
                                                    className="ml-9 space-y-4 text-gray-700 leading-relaxed cms-content"
                                                    dangerouslySetInnerHTML={{ __html: section.content }}
                                                />
                                            </section>
                                        ))}
                                    </>
                                )}
                            </div>
                        </main>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CMSPage;

